// LIB
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
// MODELS
import { ContractItem } from '@core/models/contract-item.model';
import { ContractPack } from '@core/models/contract-pack/contract-pack.model';
import { Contract } from '@core/models/contract.model';
// SERVICES
import { ContractPackService } from '@core/services/contract-pack/contract-pack.service';
// COMPONENTS
import { ThemaPackManagingDialogComponent } from '@shared/contract/contract-item/contract-thema-nego/thema-pack-managing-dialog/thema-pack-managing-dialog.component';
import { ConfirmDialogComponent } from '@shared/dialog/confirm-dialog/confirm-dialog.component';
import moment from 'moment';

@Component({
  standalone: false,
  selector: 'app-thema-list-pack-dialog',
  templateUrl: './thema-list-pack-dialog.component.html',
  styleUrls: ['./thema-list-pack-dialog.component.scss'],
})
export class ThemaListPackDialogComponent implements OnInit, OnDestroy {
  public componentDestroyed$: Subject<void> = new Subject();
  public contractPacks$: Observable<ContractPack[]>;
  public filteredContractPacks$: Observable<ContractPack[]>;
  public isPacksLoading: boolean;
  public searchPackFormControl = new FormControl(null);
  public selectedYearFormControl = new FormControl(null);
  public availableYears = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract: Contract;
      contractItem: ContractItem;
    },
    public dialog: MatDialog,
    private contractPackService: ContractPackService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.isPacksLoading = true;
    this.getPacks();
    this.getAvailableYears();
    this.handleSearchPackValueChange();
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.unsubscribe();
  }

  public closeDialog(): void {
    this.dialog.closeAll();
  }

  public getPacks(selectedYear = null): void {
    const filter = {
      year: selectedYear ? selectedYear.toString() : this.data.contractItem.year.toString(),
    };

    this.contractPackService.getList(filter).subscribe((contractPacks: ContractPack[]) => {
      contractPacks.sort((a, b) => a.id - b.id);
      this.contractPacks$ = of(contractPacks);
      this.filteredContractPacks$ = of(contractPacks);
      this.isPacksLoading = false;
    });
  }

  // return au table of N-1, N and N+1
  public getAvailableYears(): void {
    const currentYear = Number(moment().format('YYYY'));
    this.availableYears = [currentYear - 1, currentYear, currentYear + 1];
    this.selectedYearFormControl.setValue(Number(this.data.contractItem.year));

    this.selectedYearFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.isPacksLoading = true;
      this.getPacks(value);
    });
  }

  public filterPackList(value: string): ContractPack[] {
    const filterValue = value.toLowerCase();
    let filteredPacks: ContractPack[] = [];

    this.contractPacks$.subscribe(contractPacks => {
      filteredPacks = contractPacks.filter((pack: ContractPack) => pack.label?.toLowerCase().includes(filterValue));
    });

    return filteredPacks;
  }

  public handleSearchPackValueChange() {
    this.searchPackFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      this.searchPackFormControl.patchValue(value.toUpperCase(), { emitEvent: false });
      this.filteredContractPacks$ = of(this.filterPackList(value));
    });
  }

  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  public addNewpackToPackList(pack): void {
    this.contractPacks$.subscribe(packs => {
      packs.push(pack);
      this.contractPacks$ = of(packs);
      this.filteredContractPacks$ = of(packs);
    });
  }

  public getFullListTooltip(item): string {
    return item.contractPackChannels.map(channelPack => `${channelPack.channel.channelCode} - ${channelPack.rate}%`).join(', ');
  }

  public openThemaPackManagingModal($modalMode, $pack = null): void {
    this.dialog
      .open(ThemaPackManagingDialogComponent, {
        data: {
          contract: this.data.contract,
          contractItem: this.data.contractItem,
          modalMode: $modalMode,
          pack: $pack,
        },
        panelClass: 'tv-dialog-container',
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.getPacks(this.selectedYearFormControl.value);
        }
      });
  }

  public duplicatePack(item): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Êtes-vous sûr de vouloir dupliquer le pack ',
        content: `"${item.label}" ?`,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isPacksLoading = true;
        const packChannels = item.contractPackChannels.map(contractPackChannel => {
          return {
            channel: contractPackChannel.channel.id,
            rate: contractPackChannel.rate,
          };
        });
        const data = {
          label: item.label + ' - Copie',
          year: this.data.contractItem.year,
          isActive: 1,
          defTx: item.defTx,
          contractPackChannels: packChannels,
        };

        this.contractPackService.createPack(data).subscribe(
          () => {
            this.getPacks(this.selectedYearFormControl.value);
            this.displaySnackBar('Pack dupliqué avec succès', 'tv-chip-success');
          },
          error => {
            this.displaySnackBar('Erreur lors de la création du pack', 'tv-chip-error');
            console.error('Error saving pack', error);
          }
        );
      }
    });
  }

  public deletePack(item): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Êtes-vous sûr de vouloir supprimer le pack ',
        content: `"${item.label}" ?`,
      },
      panelClass: 'tv-dialog-container',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isPacksLoading = true;
        this.contractPackService.deletePack(item.id).subscribe(
          () => {
            this.getPacks(this.selectedYearFormControl.value);
            this.displaySnackBar('Le Pack a été supprimé', 'tv-chip-success');
          },
          error => {
            if (error.status === 400) {
              this.displaySnackBar('Ce pack ne peut pas être supprimé car il est lié à un contrat', 'tv-chip-error');
              this.isPacksLoading = false;

              return;
            }
            console.error('Error deleting pack', error);
            this.displaySnackBar('Erreur de suppression', 'tv-chip-error');
            this.isPacksLoading = false;
          }
        );
      }
    });
  }
}
